import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import {
  makeStyles,
  createStyles,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
} from '@material-ui/core'
import { KeyboardArrowLeft, KeyboardArrowRight } from '@material-ui/icons'
import Link from '../Link/Link'
import { connect } from 'react-redux'
import Glider from 'react-glider'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import { calcPackMinPrice, truncateString } from '../../utils/utils'
import Button from '../Button/Button'
import SizeModal from '../Sizes/SizeModal'
import ProductCard from '../Shop/ProductCard'
import ImgKitHandler from '../ImgKitHandler/ImgKitHandler'
import CMSText, { isPrismicText, prismicText } from '../Global/CMSText'
import { usePackAvailability } from '../../utils/usePackAvailability'
import PackPriceFormat from './PackPriceFormat'
import { savePackOrigin } from '../../redux/actions/packs'
import { useCart } from '../../hooks/useCart'
import {
  isSecondHandOnly,
  SECOND_HAND_TAXON_SLUGS,
} from '../../utils/constants'
import { useRouter } from 'next/router'
import { RootState } from '../../redux/reducers/rootReducer'
import { GetMapDispatchTypes } from '@/interfaces/common/redux'

export const useStyles = makeStyles((theme) =>
  createStyles({
    row: {
      display: 'flex',
      margin: theme.spacing(0, 0, 2),
      [theme.breakpoints.up('lg')]: {
        margin: 0,
      },
    },
    // Common Pack Style
    packAvailability: {
      display: 'inline-block',
      marginBottom: theme.spacing(2),
      padding: theme.spacing(0.5, 1),
      borderRadius: theme.spacing(2),
      background: theme.palette.common.black,
      color: theme.palette.common.white,
    },
    packTitleLayout: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: theme.spacing(0.5),
    },
    packContent: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
    },
    packCarousel: {
      '& .glider-prev': {
        left: 0,
      },
      '& .glider-next': {
        right: 0,
      },
      '& .glider-contain': {
        paddingLeft: 32,
        paddingRight: 32,
        [theme.breakpoints.up('md')]: {
          paddingLeft: 40,
          paddingRight: 40,
        },
      },
    },
    packCarouselSlide: {
      margin: theme.spacing(0, 1),
    },
    packCarouselMedia: {},
    packTop: {
      position: 'relative',
    },
    packTopLink: {
      color: theme.palette.common.black,
      '&:before': {
        zIndex: 1,
        content: "''",
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    packBottom: {},
    packDesc: {
      margin: theme.spacing(1, 0),
      color: theme.extraColors.greyText,
      lineHeight: 1.4,
      [theme.breakpoints.down('lg')]: {
        fontSize: theme.typography.pxToRem(12),
      },
      [theme.breakpoints.up('lg')]: {
        minHeight: 40,
      },
      [theme.breakpoints.up('xl')]: {
        minHeight: 76,
      },
    },
    // Pack Sport Specific Style
    packSport: {},
    packSportHead: {
      position: 'relative',
      zIndex: 2,
      padding: theme.spacing(3, 2),
      boxShadow: theme.shadows[1],
      borderTop: `3px solid ${theme.palette.primary.main}`,
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.common.white,
    },
    packSportContent: {
      paddingLeft: theme.spacing(2),
    },
    packSportBottom: {},
    packSportFoot: {
      position: 'relative',
      textAlign: 'center',
      zIndex: 1,
      marginTop: -20,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(4, 2, 2, 2),
      boxShadow: theme.shadows[1],
      backgroundColor: theme.extraColors.greyLight,
      overflow: 'hidden',
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'center',
    },
    packSportTitle: {
      display: 'flex',
      flexDirection: 'column',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
    },
    // Pack Fashion Specific Style
    packFashion: {
      overflow: 'hidden',
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.shadows[1],
      '& .glider-contain': {
        '& .glider-next, & .glider-prev': {
          opacity: 0,
        },
        '&:hover, &:focus': {
          '& .glider-next, & .glider-prev': {
            opacity: 1,
            '&[aria-disabled=true]': {
              opacity: 0.3,
            },
          },
        },
      },
    },
    packFashionContent: {
      padding: theme.spacing(2),
      [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(5, 3),
      },
    },
    packFashionTitle: {},
    packBottomFashion: {
      display: 'flex',
      gap: theme.spacing(1),
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    // Fake Pack Specific Style
    fakePack: {
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.shadows[1],
    },
    fakePackContent: {
      position: 'relative',
      zIndex: 2,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(3, 2),
      boxShadow: theme.shadows[1],
      background: theme.palette.primaryTransparent.light,
      [theme.breakpoints.up('sm')]: {
        minHeight: '282px',
      },
    },
    fakePackHead: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing(2),
      padding: theme.spacing(0, 2),
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
        display: 'block',
      },
    },
    fakePackFoot: {
      padding: theme.spacing(2, 0, 0),
      textAlign: 'center',
    },
    // Partner Push inside Pack List
    push: {
      overflow: 'hidden',
      borderRadius: theme.shape.borderRadius,
      position: 'relative',
      boxShadow: theme.shadows[1],
      [theme.breakpoints.up('md')]: {
        minHeight: '100%', // Allow car to fit height of pack cards (keep image ratio on mobile)
      },
    },
    pushImg: {
      display: 'block',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      [theme.breakpoints.up('md')]: {
        position: 'absolute',
        zIndex: 1,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
      },
    },
    pushContent: {
      position: 'absolute',
      zIndex: 2,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      rowGap: theme.spacing(2),
      padding: theme.spacing(3),
      background: 'rgba(0, 0, 0, .25)',
    },
    pushTitle: {
      textAlign: 'center',
      color: theme.palette.common.white,
    },
    whiteSpace: {
      whiteSpace: 'nowrap',
    },
  })
)

type Tprops = {
  pack: any
  cart: any
  settings: any
  latestProducts: any
  index: number
  text: any
  thumbRatio: any
  thumbWidth?: number
  cardWidth?: number
  packLayout: string
  t?: any
  stock: any
  numberPack: number
  promotions: any
  packPush?: any
  takeback: any
  savePackOrigin?: (origin) => void
} & ReturnType<typeof mapStateToProps> &
  GetMapDispatchTypes<typeof mapDispatchToProps>

type PackCarouselProps = {
  isBig?: boolean
}

const PacksCard = ({
  index,
  pack,
  cart,
  settings,
  stock,
  text,
  thumbRatio,
  thumbWidth,
  cardWidth,
  packLayout,
  promotions,
  t,
  numberPack,
  latestProducts,
  packPush,
  takeback,
  takeBackLP,
  savePackOrigin,
}: Tprops) => {
  const { addMultipleProductToCart } = useCart()
  const classes = useStyles()
  const theme = useTheme()
  const [openModal, setOpenModal] = useState(false)
  const [minPrice, setMinPrice] = useState(0)
  const { trackEvent } = useMatomo()
  const router = useRouter()
  const packState = usePackAvailability(
    settings?.add_pack_if_complete,
    pack?.packProducts,
    stock
  )
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isHtmlDescription = text?.is_html_description
  const preventAddingToCart = settings?.prevent_adding_to_cart_without_details
  const discountPriceFormat = settings?.discounted_price_display_format
  const [selection, setSelection] = useState([])

  const category = router?.query?.slug
  const isSecondHandTaxon =
    isSecondHandOnly === 'true'
      ? true
      : SECOND_HAND_TAXON_SLUGS.includes(category?.[0])

  useEffect(() => {
    const minPrice = calcPackMinPrice(pack?.packProducts)
    setMinPrice(minPrice)
  }, [promotions?.currentPromotion])

  // add pack with all variants
  const AddMultipleProductsToCart = async (selection) => {
    // add selection to cart
    trackEvent({
      category: 'Packs',
      action: `Add products from packs in Cart`,
    })
    await addMultipleProductToCart({ items: selection })
    setSelection([])
    setOpenModal(false)
  }

  const setOpenModalAndTrack = () => {
    setOpenModal(true)
    trackEvent({
      category: 'Packs',
      action: `Open Pack ${pack.code} modale`,
    })
  }

  const goToPackAndTrack = () => {
    trackEvent({
      category: 'Packs',
      action: `Go to Pack: ${pack.slug}`,
    })
    savePackOrigin('packs')
  }

  const PackCarousel = ({ isBig = false }: PackCarouselProps) => {
    return (
      <div className={clsx('pack__carousel', classes.packCarousel)}>
        <Glider
          hasArrows
          draggable
          scrollLock
          slidesToShow={4}
          iconLeft={<KeyboardArrowLeft />}
          iconRight={<KeyboardArrowRight />}
        >
          {pack?.packProducts?.map((product) => (
            <div
              key={`packCarouselSlide-${product.product.code}`}
              className={classes.packCarouselSlide}
            >
              <div
                data-ratio={thumbRatio}
                className={classes.packCarouselMedia}
              >
                <ImgKitHandler
                  imagePaths={product.product?.images[0]}
                  settings={{
                    quality: 75,
                    width: isBig ? cardWidth : thumbWidth,
                    ar: thumbRatio?.substring(3),
                  }}
                />
              </div>
            </div>
          ))}
        </Glider>
      </div>
    )
  }

  const PackTitle = () => (
    <div className={clsx('packCard-title__layout', classes.packTitleLayout)}>
      <Typography variant="h3" component="h2">
        <Link href={`./packs/${pack.slug}`}>
          <a className={clsx('packCard-title__link', classes.packTopLink)}>
            {pack.name}
          </a>
        </Link>
      </Typography>
      {packState.soldOut && (
        <Typography
          className={clsx(
            'packCard-title__availability',
            classes.packAvailability,
            `packCard-title__availability__${pack?.code}`
          )}
          variant="caption"
        >
          <CMSText
            asText
            data={text?.sold_out}
            defaultText={t('texts:packs:victim_of_success')}
          />
        </Typography>
      )}
    </div>
  )

  const PackDesc = () => (
    <Typography
      variant="body1"
      component="div"
      className={`pack__desc ${classes.packDesc}`}
    >
      {pack.description && (
        <div
          dangerouslySetInnerHTML={{
            __html: truncateString(
              pack.description,
              useMediaQuery(theme.breakpoints.up('lg')) ? 250 : 100,
              isHtmlDescription
            ),
          }}
        />
      )}
    </Typography>
  )

  const PackActions = () => (
    <>
      {settings?.add_a_pack_from_pack_page &&
        !packState.soldOut &&
        !preventAddingToCart && (
          <Button
            size={isMobile ? 'small' : 'medium'}
            variant="contained"
            color={'secondary'}
            onClick={() => setOpenModalAndTrack()}
            className={'pack__cta'}
            data-testid={`select-pack-${pack.id}`}
          >
            <CMSText
              asText
              data={text?.select}
              defaultText={t('texts:packs:select')}
            />
          </Button>
        )}
      <Link href={`./packs/${pack.slug}`}>
        <a onClick={() => goToPackAndTrack()}>
          <Button
            size={isMobile ? 'small' : 'medium'}
            component="span"
            variant="contained"
            color={'primary'}
            className={'pack__link'}
            data-testid={`go-to-pack-${pack.id}`}
          >
            <CMSText
              asText
              data={text?.link_to_single}
              defaultText={t('texts:packs:link_to_single')}
            />
          </Button>
        </a>
      </Link>
    </>
  )

  return (
    <>
      {packPush &&
        packPush.length > 0 &&
        index === packPush[0].list_position - 1 && (
          <Grid item xs={12} lg={6} key="pack-push">
            <div className={`pack-push ${classes.push}`}>
              <Link
                href={
                  packPush[0]?.cta_link?.url ? packPush[0]?.cta_link?.url : '/'
                }
              >
                <a target={packPush[0]?.cta_link?.target ? '_blank' : '_self'}>
                  <div className={`pack-push__content ${classes.pushContent}`}>
                    {isPrismicText(packPush[0]?.title) && (
                      <Typography
                        variant={'h2'}
                        className={`pack-push__title ${classes.pushTitle}`}
                      >
                        {prismicText(packPush[0]?.title)}
                      </Typography>
                    )}
                    {isPrismicText(packPush[0]?.cta_label) && (
                      <Button
                        variant={'contained'}
                        color={'primary'}
                        className={'pack-push__btn'}
                      >
                        {prismicText(packPush[0]?.cta_label)}
                      </Button>
                    )}
                  </div>
                  <img
                    src={packPush[0]?.image?.url}
                    alt={''}
                    className={classes.pushImg}
                  />
                </a>
              </Link>
            </div>
          </Grid>
        )}
      <Grid item xs={12} lg={6} key={`pack-${pack.id}`}>
        {packLayout === 'Sport' && (
          <article className={`packSport ${classes.packSport}`}>
            <div className={`packSport__head ${classes.packSportHead}`}>
              <Grid container spacing={0} className={classes.row}>
                {pack.images.length > 0 && (
                  <Grid xs={4} item>
                    <Link href={`./packs/${pack.slug}`}>
                      <a>
                        <div data-ratio={thumbRatio}>
                          <ImgKitHandler
                            imagePaths={pack.images[0]}
                            alt={''}
                            settings={{
                              quality: 75,
                              width: cardWidth,
                              ar: thumbRatio?.substring(3),
                            }}
                          />
                        </div>
                      </a>
                    </Link>
                  </Grid>
                )}
                <Grid xs={pack.images.length > 0 ? 8 : 12} item>
                  <div
                    className={`${classes.packContent} ${classes.packSportContent}`}
                  >
                    <div className={classes.packTop}>
                      <div className={`pack__title ${classes.packSportTitle}`}>
                        <PackTitle />
                        <div className={`packCard-title__price__${pack?.code}`}>
                          <PackPriceFormat
                            promotions={promotions}
                            settings={settings}
                            minPrice={minPrice}
                            discountPriceFormat={discountPriceFormat}
                          />
                        </div>
                      </div>
                      <PackDesc />
                    </div>
                    <div className={classes.packBottom}>
                      {useMediaQuery(theme.breakpoints.up('md')) && (
                        <PackCarousel />
                      )}
                    </div>
                  </div>
                </Grid>
              </Grid>
              {isMobile && <PackCarousel />}
            </div>
            <div
              className={clsx('packSport__foot', classes.packSportFoot)}
              data-stockloaded={stock?.loading ? 'loading' : 'loaded'}
            >
              <PackActions />
            </div>
          </article>
        )}
        {packLayout === 'Sport Full' && (
          <article
            className={clsx('packSport', 'packSport--full', classes.packSport)}
          >
            <div className={clsx('packSport__head', classes.packSportHead)}>
              <Grid container spacing={0} className={classes.row}>
                <Grid xs={12} item>
                  <div className={`${classes.packContent}`}>
                    <div className={classes.packTop}>
                      <div className={`pack__title ${classes.packSportTitle}`}>
                        <PackTitle />
                        <div>
                          <PackPriceFormat
                            promotions={promotions}
                            settings={settings}
                            minPrice={minPrice}
                            discountPriceFormat={discountPriceFormat}
                          />
                        </div>
                      </div>
                      <PackDesc />
                    </div>
                    <div className={classes.packSportBottom}>
                      <PackCarousel />
                    </div>
                  </div>
                </Grid>
              </Grid>
            </div>
            <div
              className={clsx('packSport__foot', classes.packSportFoot)}
              data-stockloaded={stock?.loading ? 'loading' : 'loaded'}
            >
              <PackActions />
            </div>
          </article>
        )}
        {packLayout === 'Fashion' && (
          <article className={`packFashion ${classes.packFashion}`}>
            <Grid container spacing={0} className={classes.row}>
              <Grid xs={6} item>
                <Glider
                  hasArrows
                  draggable
                  scrollLock
                  slidesToShow={1}
                  iconLeft={<KeyboardArrowLeft />}
                  iconRight={<KeyboardArrowRight />}
                >
                  {pack.images.length > 0 && (
                    <div>
                      <div data-ratio={thumbRatio}>
                        <ImgKitHandler
                          imagePaths={pack.images[0]}
                          alt={''}
                          settings={{
                            quality: 75,
                            width: cardWidth,
                            ar: thumbRatio?.substring(3),
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {pack?.packProducts?.map((product) => (
                    <div key={`packCarouselSlide-${product?.product?.code}`}>
                      <div data-ratio={thumbRatio}>
                        <ImgKitHandler
                          imagePaths={product?.product?.images[0]}
                          alt={''}
                          settings={{
                            quality: 75,
                            width: cardWidth,
                            ar: thumbRatio?.substring(3),
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </Glider>
              </Grid>
              <Grid xs={6} item>
                <div
                  className={`pack__fashionContent ${classes.packContent} ${classes.packFashionContent}`}
                >
                  <div className={classes.packTop}>
                    <div className={`pack__title ${classes.packFashionTitle}`}>
                      <PackTitle />
                      <PackPriceFormat
                        promotions={promotions}
                        settings={settings}
                        minPrice={minPrice}
                        discountPriceFormat={discountPriceFormat}
                      />
                    </div>
                    <PackDesc />
                  </div>
                  <div
                    className={clsx(
                      classes.packBottom,
                      classes.packBottomFashion
                    )}
                    data-stockloaded={stock?.loading ? 'loading' : 'loaded'}
                  >
                    <PackActions />
                  </div>
                </div>
              </Grid>
            </Grid>
          </article>
        )}
      </Grid>
      {index === numberPack - 1 && (
        <Grid item xs={12} lg={6} key="pack-fake">
          <article className={classes.fakePack}>
            <div className={classes.fakePackContent}>
              <div className={classes.fakePackHead}>
                <Typography variant={'h3'} color={'primary'}>
                  <CMSText
                    asText
                    data={text?.fake_pack_title?.text}
                    defaultText={t('texts:packs:fake_pack_title')}
                  />
                </Typography>
                {useMediaQuery(theme.breakpoints.up('sm')) && (
                  <Link href="/shop">
                    <a>
                      <Button
                        variant={'outlined'}
                        color={'primary'}
                        size={'small'}
                        onClick={() =>
                          trackEvent({
                            category: 'Packs',
                            href: `/shop`,
                            action: `Fake pack to shop`,
                          })
                        }
                      >
                        <CMSText
                          asText
                          data={text?.fake_pack_button}
                          defaultText={t('texts:packs:fake_pack_button')}
                        />
                      </Button>
                    </a>
                  </Link>
                )}
              </div>
              {latestProducts?.length > 0 && (
                <div className={classes.packCarousel}>
                  <Glider
                    hasArrows
                    draggable
                    scrollLock
                    slidesToShow={4}
                    iconLeft={<KeyboardArrowLeft />}
                    iconRight={<KeyboardArrowRight />}
                  >
                    {latestProducts?.map((product) => (
                      <div
                        key={`packCarouselSlide-${product.code}`}
                        className={classes.packCarouselSlide}
                      >
                        <ProductCard
                          name={product.name}
                          text={text}
                          thumb={product?.images[0]}
                          thumbRatio={thumbRatio}
                          thumbWidth={cardWidth}
                          productCode={product.code}
                          product={product}
                          small
                          slug={product.slug}
                          addButton={false}
                          settings={settings}
                        />
                      </div>
                    ))}
                  </Glider>
                </div>
              )}
              {isMobile && (
                <div className={classes.fakePackFoot}>
                  <Link href="/shop">
                    <a>
                      <Button
                        size={'small'}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() =>
                          trackEvent({
                            category: 'Packs',
                            href: `/shop`,
                            action: `mobile fake pack to shop`,
                          })
                        }
                      >
                        <CMSText
                          asText
                          data={text?.fake_pack_button}
                          defaultText={t('texts:packs:fake_pack_button')}
                        />
                      </Button>
                    </a>
                  </Link>
                </div>
              )}
            </div>
          </article>
        </Grid>
      )}
      <SizeModal
        open={openModal}
        handleClose={() => setOpenModal(false)}
        type={'pack'}
        text={text}
        stock={stock}
        products={pack.packProducts}
        cart={cart}
        AddMultipleProductsToCart={AddMultipleProductsToCart}
        thumbRatio={thumbRatio}
        thumbWidth={thumbWidth}
        isMobile={isMobile}
        settings={settings}
        selection={selection}
        setSelection={setSelection}
        isSecondHandTaxon={isSecondHandTaxon}
        takeback={takeback}
        takeBackLP={takeBackLP}
      />
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  cart: state.cart,
  stock: state.stock,
  promotions: state.promotions,
  takeBackLP: state.takeBack,
})

const mapDispatchToProps = {
  savePackOrigin: savePackOrigin,
}

export default connect(mapStateToProps, mapDispatchToProps)(PacksCard)
